import {
  LoadingSpinner,
  PlainButton,
  TextInput,
} from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { Button } from "@jugl-web/ui-kit";
import {
  useRestApiProvider,
  type AgentConfigData,
  type AgentInstructionOptionId,
  type AgentTool,
  type CustomTemplateData,
} from "@jugl-web/rest-api";
import { DEFAULT_AGENT_CONFIG } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import { useToast, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useEffect, useState } from "react";
import { GenericToolsSection } from "./components/GenericToolsSection";
import { IntegrationToolsSection } from "./components/IntegrationToolsSection";
import { CustomToolDialog } from "./components/CustomToolDialog";

export const CXAgentSetup: React.FC = () => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();

  const { data: agentConfig, isLoading } =
    integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [createConfig, { isLoading: isCreating }] =
    integrationsApi.useCreateAgentConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] =
    integrationsApi.useUpdateAgentConfigMutation();
  const { data: allConnections } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });
  const connections = allConnections?.data ?? [];

  const [businessName, setBusinessName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [genericTools, setGenericTools] = useState<string[]>([]);
  const [integrationTools, setIntegrationTools] = useState<AgentTool[]>([]);
  const [customTools, setCustomTools] = useState<CustomTemplateData[]>([]);
  const [customDialogIdx, setCustomDialogIdx] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (agentConfig && !initialized) {
      const cfg = agentConfig.config;
      setBusinessName(cfg.business_name ?? "");
      setInstructions(cfg.instructions[0]?.value ?? "");

      const generic = cfg.tools?.find((tool) => tool.pack === "generic");
      setGenericTools(generic?.tools ?? []);

      setIntegrationTools(
        cfg.tools?.filter(
          (tool) =>
            tool.pack !== "generic" &&
            tool.pack !== "custom" &&
            !!tool.connection_id
        ) ?? []
      );

      setCustomTools(
        cfg.tools
          ?.filter((tool) => tool.pack === "custom")
          .map((tool) => tool.custom_template_data!)
          .filter(Boolean) ?? []
      );

      setInitialized(true);
    }
  }, [agentConfig, initialized]);

  const buildToolsPayload = useCallback((): AgentTool[] => {
    const tools: AgentTool[] = [];

    if (genericTools.length > 0) {
      tools.push({ pack: "generic", tools: genericTools, params: {} });
    }

    tools.push(
      ...integrationTools.map((tool) => {
        if (!tool.params) {
          tool.params = {};
        }
        return tool;
      })
    );

    customTools.forEach((tmpl) => {
      tools.push({ pack: "custom", custom_template_data: tmpl });
    });

    return tools;
  }, [genericTools, integrationTools, customTools]);

  const handleSave = useCallback(async () => {
    const existingInstructions =
      agentConfig?.config.instructions ?? DEFAULT_AGENT_CONFIG.instructions;
    const selectedInstructionOption: AgentInstructionOptionId =
      existingInstructions[0]?.name ?? "custom-prompt";
    const nextInstructions = [
      {
        name: selectedInstructionOption,
        value: instructions,
      },
      ...existingInstructions.filter(
        (item) => item.name !== selectedInstructionOption
      ),
    ];

    const config: AgentConfigData = {
      ...(agentConfig?.config ?? DEFAULT_AGENT_CONFIG),
      business_name: businessName,
      instructions: nextInstructions,
      tools: buildToolsPayload(),
    };

    try {
      if (agentConfig) {
        await updateConfig({ entityId, config }).unwrap();
      } else {
        await createConfig({ entityId, config }).unwrap();
      }
      toast(
        t({
          id: "cx-agent-setup.save-success",
          defaultMessage: "Agent configuration saved",
        })
      );
    } catch {
      toast(
        t({
          id: "cx-agent-setup.save-error",
          defaultMessage: "Failed to save configuration",
        })
      );
    }
  }, [
    agentConfig,
    businessName,
    instructions,
    buildToolsPayload,
    entityId,
    createConfig,
    updateConfig,
    toast,
    t,
  ]);

  const handleCustomToolSave = (data: CustomTemplateData) => {
    if (customDialogIdx !== null && customDialogIdx < customTools.length) {
      setCustomTools((prev) =>
        prev.map((item, i) => (i === customDialogIdx ? data : item))
      );
    } else {
      setCustomTools((prev) => [...prev, data]);
    }
    setCustomDialogIdx(null);
  };

  const removeCustomTool = (idx: number) =>
    setCustomTools((prev) => prev.filter((_, i) => i !== idx));

  const isSaving = isCreating || isUpdating;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 p-12">
      <div className="flex flex-col gap-10 rounded-2xl bg-white p-8">
        {/* Business Info */}
        <div className="flex flex-col gap-4">
          <span className="text-dark font-secondary font-medium">
            {t({
              id: "cx-agent-setup.business-info",
              defaultMessage: "Business Info",
            })}
          </span>
          <TextInput
            labelPosition="border-overlap"
            label={t({
              id: "cx-agent-setup.business-name",
              defaultMessage: "Business Name",
            })}
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value.trimStart())}
          />
        </div>

        {/* Instructions */}
        <div className="flex flex-col gap-4">
          <span className="text-dark font-secondary font-medium">
            {t({
              id: "cx-agent-setup.instructions",
              defaultMessage: "Instructions",
            })}
          </span>
          <TextAreaInput
            label={t({
              id: "cx-agent-setup.instructions-label",
              defaultMessage: "Agent Instructions",
            })}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            classNames={{ inputClassName: "min-h-[160px]" }}
          />
        </div>

        {/* Generic Tools */}
        <div className="flex flex-col gap-4">
          <span className="text-dark font-secondary font-medium">
            {t({
              id: "cx-agent-setup.generic-tools",
              defaultMessage: "Generic Tools",
            })}
          </span>
          <GenericToolsSection
            selected={genericTools}
            onChange={setGenericTools}
          />
        </div>

        {/* Integration Tools */}
        <div className="flex flex-col gap-4">
          <span className="text-dark font-secondary font-medium">
            {t({
              id: "cx-agent-setup.integration-tools",
              defaultMessage: "Integration Tools",
            })}
          </span>
          <IntegrationToolsSection
            connections={connections}
            tools={integrationTools}
            onChange={setIntegrationTools}
          />
        </div>

        {/* Custom Tools */}
        <div className="flex flex-col gap-4">
          <span className="text-dark font-secondary font-medium">
            {t({
              id: "cx-agent-setup.custom-tools",
              defaultMessage: "Custom Tools",
            })}
          </span>
          <div className="flex flex-col gap-3">
            {customTools.map((tmpl, idx) => (
              <div
                key={+idx}
                className="flex items-center justify-between rounded-xl border border-solid border-[#F2F2F2] px-4 py-3"
              >
                <div className="flex flex-col">
                  <span className="font-secondary text-sm font-medium text-[#383838]">
                    {tmpl.name || `Custom Tool ${idx + 1}`}
                  </span>
                  <span className="text-dark-600 text-xs">
                    {tmpl.method} {tmpl.url}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PlainButton
                    className="text-primary font-secondary text-sm"
                    onClick={() => setCustomDialogIdx(idx)}
                  >
                    {t({ id: "common.edit", defaultMessage: "Edit" })}
                  </PlainButton>
                  <PlainButton
                    className="text-tertiary-800 font-secondary text-sm"
                    onClick={() => removeCustomTool(idx)}
                  >
                    {t({ id: "common.remove", defaultMessage: "Remove" })}
                  </PlainButton>
                </div>
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCustomDialogIdx(customTools.length)}
            >
              {t({
                id: "cx-agent-setup.add-custom-tool",
                defaultMessage: "Add Custom Tool",
              })}
            </Button>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-4">
          <Button onClick={handleSave} isLoading={isSaving}>
            {t({ id: "common.save", defaultMessage: "Save" })}
          </Button>
        </div>
      </div>

      {customDialogIdx !== null && (
        <CustomToolDialog
          isOpen
          onClose={() => setCustomDialogIdx(null)}
          onSave={handleCustomToolSave}
          initial={
            customDialogIdx < customTools.length
              ? customTools[customDialogIdx]
              : undefined
          }
        />
      )}
    </div>
  );
};
